import { i18n } from "../../../translate/i18n";
import React, { memo, useEffect } from "react";
import { Handle, useUpdateNodeInternals } from "react-flow-renderer";
import { Box, Typography } from "@mui/material";
import CallSplitIcon from '@mui/icons-material/CallSplitOutlined';
import { NodeWrapper } from "./NodeWrapper";
import { mkHeaderStyle, HEADER_ICON_STYLE, HEADER_TEXT_STYLE, NODE_BODY_STYLE, mkHandleStyle } from "./nodeStyles";


import { Tooltip, IconButton } from '@material-ui/core';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import { HelpOutline as HelpOutlineIcon } from '@mui/icons-material';

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: '#1e293b',
    color: '#f8fafc',
    maxWidth: 380,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #334155',
    padding: '12px 14px',
    borderRadius: '8px',
    fontFamily: "'Inter', 'Roboto', sans-serif",
    lineHeight: 1.5,
  },
  arrow: { color: '#1e293b' },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
  pulseIcon: {
    color: '#ffffff',
    borderRadius: '50%',
    animation: '$glowPulse 1.5s infinite',
  },
  '@keyframes glowPulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.9)' },
    '70%': { boxShadow: '0 0 0 8px rgba(255, 255, 255, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)' },
  },
}));

export default memo(({ data, isConnectable, id, selected }) => {
  const classes = useStyles();
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, updateNodeInternals, data.conditions]);

  const renderValue = val => {
    if (!val) return <span style={{ fontStyle: 'italic', color: '#999' }}>vazio</span>;
    if (val.includes("${")) {
      return <span style={{ color: '#9c27b0', fontWeight: 'bold' }}>{val}</span>;
    }
    return val;
  };

  const conditions = data.conditions && data.conditions.length > 0 ? data.conditions : data.leftValue ? [{
    id: 'true',
    leftValue: data.leftValue,
    operator: data.operator,
    rightValue: data.rightValue
  }] : [];

  return (
    <NodeWrapper id={id} selected={selected} color="#D500F9">
      <Handle 
        type="target" 
        position="left" 
        style={mkHandleStyle("#D500F9", { left: "-12px", top: "20px", transform: "translateY(-50%)" })} 
        isConnectable={isConnectable} 
      />

      <div style={{ ...mkHeaderStyle("#D500F9"), justifyContent: "space-between" }}>
  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <CallSplitIcon style={HEADER_ICON_STYLE} />
        <div style={HEADER_TEXT_STYLE}>{data.name || "Condición Lógica"}</div>
      </div>
  <div style={{ display: "flex", alignItems: "center", marginLeft: "auto" }}>
    <HtmlTooltip
            title={
              <React.Fragment>
                <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', color: '#38bdf8' }}>
                  💡 {i18n.t("flowBuilder.tooltips.conditionCompare.title")}
                </div>
                <div style={{ marginBottom: '0' }}>
                  <div dangerouslySetInnerHTML={{ __html: i18n.t("flowBuilder.tooltips.conditionCompare.body") }} />
                </div>
              </React.Fragment>
            }
            placement="right"
          >
            <IconButton size="small" style={{ padding: '4px', marginLeft: '2px' }} className={classes.pulseIcon}>
              <HelpOutlineIcon style={{ fontSize: '15px' }} />
            </IconButton>
          </HtmlTooltip>
  </div>
</div>

      <div style={{ ...NODE_BODY_STYLE, padding: 0, width: "100%", display: "block" }}>
        {conditions.length === 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60px' }}>
            <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>{i18n.t("flowBuilder.nodes.condition.empty") || "Ninguna condición configurada"}</Typography>
          </Box>
        ) : (
          <Box sx={{ padding: '12px', paddingBottom: '0' }}>
            {conditions.map((cond, index) => (
              <Box key={cond.id || index} sx={{
                position: 'relative',
                p: 1.5,
                mb: index < conditions.length - 1 ? 1 : 0,
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                fontSize: '12px'
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pr: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="textSecondary" fontWeight="bold">{i18n.t("flowBuilder.nodes.condition.if") || "SI"}</Typography>
                    <Typography variant="caption" sx={{ backgroundColor: '#D500F9', color: 'white', px: 1, borderRadius: '4px', fontSize: '10px' }}>
                      {cond.operator}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                    <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={cond.leftValue}>
                      {renderValue(cond.leftValue)}
                    </Box>
                    {cond.operator !== 'is_empty' && cond.operator !== 'is_not_empty' && (
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#666' }} title={cond.rightValue}>
                        <span style={{ fontSize: '10px', color: '#999', marginRight: '4px' }}>→</span>
                        {renderValue(cond.rightValue)}
                      </Box>
                    )}
                  </Box>
                </Box>
                <Handle 
                  type="source" 
                  position="right" 
                  id={cond.id || 'true'} 
                  style={mkHandleStyle("#4caf50", { right: "-25px", top: "50%", transform: "translateY(-50%)" })} 
                  isConnectable={isConnectable} 
                />
              </Box>
            ))}
          </Box>
        )}

        {/* Fallback path */}
        <Box sx={{ position: 'relative', mt: 2, pt: 1.5, pb: 1.5, px: 1.5, borderTop: '1px dashed #e0e0e0', width: "100%", boxSizing: "border-box" }}>
          <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 'bold', display: 'block' }}>{i18n.t("flowBuilder.nodes.condition.else") || "SINO (Ninguno arriba)"}</Typography>
          <Typography variant="caption" color="textSecondary">{i18n.t("flowBuilder.nodes.condition.continue") || "Continúa por aquí"}</Typography>
          <Handle 
            type="source" 
            position="right" 
            id="false" 
            style={mkHandleStyle("#D500F9", { right: "-12px", top: "50%", transform: "translateY(-50%)" })} 
            isConnectable={isConnectable} 
          />
        </Box>
      </div>
    </NodeWrapper>
  );
});
